import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ApiErrorService } from '@app/shared/services/api-error.service';
import { ApiError } from '@app/shared/types/models/api-error.model';
import { catchError, finalize, forkJoin, map, Observable, of, tap } from 'rxjs';

/**
 * LocalApiErrorHandler - In-screen API request state for a single component (typically a modal).
 *
 * Tracks saving/disabled state and parsed API errors so the host can keep the
 * screen open and show details instead of relying on the global error toast.
 *
 * Scope: Component-level (`providers` in the host component).
 *
 * Usage: Provide on a modal/component, pass Observables to {@link execute},
 * bind `isSaving` to action buttons and `errors` to {@link ApiErrorBannerComponent}.
 *
 * Architecture:
 * - {@link ApiErrorService}: Parses HTTP errors and maps error codes to i18n keys
 * - HTTP calls must pass `{ handleErrorLocally: true }` so the interceptor skips the toast
 */
@Injectable()
export class LocalApiErrorHandler {
  private readonly apiErrorService: ApiErrorService = inject(ApiErrorService);

  public readonly errors: WritableSignal<ApiError[]> = signal<ApiError[]>([]);
  public readonly isSaving: WritableSignal<boolean> = signal(false);

  /**
   * Runs one or more API calls, waits for all of them, and returns whether they all succeeded.
   * Failed calls are collected into {@link errors} instead of failing the stream.
   */
  public execute(requests: readonly Observable<unknown>[]): Observable<boolean> {
    this.errors.set([]);
    this.isSaving.set(true);

    if (requests.length === 0) {
      this.isSaving.set(false);
      return of(true);
    }

    const collectedErrors: ApiError[] = [];
    const trackedRequests = requests.map(request$ =>
      request$.pipe(
        catchError((error: unknown) => {
          collectedErrors.push(this.apiErrorService.parseOrDefault(error));
          return of(null);
        })
      )
    );

    return forkJoin(trackedRequests).pipe(
      map(() => this.uniqueByErrorCode(collectedErrors)),
      tap(uniqueErrors => this.errors.set(uniqueErrors)),
      map(uniqueErrors => uniqueErrors.length === 0),
      finalize(() => this.isSaving.set(false))
    );
  }

  private uniqueByErrorCode(errors: ApiError[]): ApiError[] {
    const seen = new Set<string>();
    const uniqueErrors: ApiError[] = [];

    for (const error of errors) {
      if (seen.has(error.errorCode)) {
        continue;
      }
      seen.add(error.errorCode);
      uniqueErrors.push(error);
    }

    return uniqueErrors;
  }
}
